export const normalizeAssignmentItem = (value) => {
  if (typeof value === 'string') return { word: value, sense_id: 'primary' };
  return {
    ...value,
    word: String(value?.word || '').trim(),
    sense_id: String(value?.sense_id || 'primary').trim(),
  };
};

export const assignmentItemKey = (value) => {
  const item = normalizeAssignmentItem(value);
  return `${item.word.toLowerCase()}::${item.sense_id}`;
};

export const assignmentTaskKey = (value) => {
  const item = normalizeAssignmentItem(value);
  const sourceMode = item.training_mode || item.trainingMode;
  const trainingMode = sourceMode === 'comprehensive' ? 'comprehensive' : 'meaning';
  return `${assignmentItemKey(item)}::${trainingMode}`;
};

export const findPracticeItem = (items, value) => {
  if (!Array.isArray(items)) return null;
  if (value && typeof value === 'object') {
    const key = assignmentItemKey(value);
    return items.find((item) => assignmentItemKey(item) === key) || null;
  }
  const key = String(value || '');
  return items.find((item) => assignmentItemKey(item) === key)
    || items.find((item) => item.word === key)
    || null;
};

export const normalizePracticeProgress = (progress, items) => Object.entries(progress || {}).reduce(
  (result, [key, value]) => {
    const item = findPracticeItem(items, key);
    if (item) result[assignmentItemKey(item)] = value;
    return result;
  },
  {},
);

export const createPracticeProgress = (items) => (items || []).reduce((result, item) => {
  result[assignmentItemKey(item)] = { stage: 1, status: 'learning', last_result: null };
  return result;
}, {});

export const selectTeacherAssignments = (values, requestedCount) => {
  const items = (Array.isArray(values) ? values : [])
    .map(normalizeAssignmentItem)
    .filter((item) => item.word);
  const parsed = Number.parseInt(requestedCount, 10);
  const limit = Number.isFinite(parsed) && parsed > 0
    ? Math.min(parsed, items.length)
    : items.length;
  return items.slice(0, limit);
};

export const normalizeRequestedCount = (value, availableCount, fallback = 1) => {
  const maximum = Math.max(1, Number.parseInt(availableCount, 10) || 1);
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return Math.min(maximum, Math.max(1, Number.parseInt(fallback, 10) || 1));
  }
  return Math.min(maximum, parsed);
};

export const parseManualPracticeItems = (value) => String(value || '')
  .split(/[\n,;，；]+/)
  .map((entry) => {
    const [word, senseId] = entry.split('::').map((part) => part.trim());
    return {
      word,
      sense_id: senseId || 'primary',
    };
  })
  .filter((item) => item.word);

export const isPracticeStartDisabled = (status) => status === 'upcoming';

const PRACTICE_TODO_ORDER = {
  in_progress: 0,
  overdue: 1,
  not_started: 2,
  pending: 2,
  upcoming: 3,
};

export const pendingPracticeAssignments = (practices) => (Array.isArray(practices) ? practices : [])
  .filter((item) => item?.assignment?.id && item.status !== 'completed')
  .sort((left, right) => {
    const statusDifference = (PRACTICE_TODO_ORDER[left.status] ?? 2) - (PRACTICE_TODO_ORDER[right.status] ?? 2);
    if (statusDifference !== 0) return statusDifference;
    const leftDueAt = left.assignment?.config?.due_at || '9999-12-31';
    const rightDueAt = right.assignment?.config?.due_at || '9999-12-31';
    return String(leftDueAt).localeCompare(String(rightDueAt));
  });

export const buildPracticeLaunchUrl = (launch) => {
  const params = new URLSearchParams();
  Object.entries(launch?.payload || {}).forEach(([key, value]) => {
    if (value != null && key !== 'items') params.set(key, String(value));
  });
  if (launch?.mode) params.set('mode', launch.mode);
  const query = params.toString();
  const route = launch?.route || (
    launch?.practice_type === 'ielts_speaking'
      ? '/student/self-check/ielts-speaking'
      : '/student/self-check'
  );
  return `${route}${query ? `?${query}` : ''}`;
};
